/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import {
  Confirmation,
  DialogService,
  getControlValue,
  isControlDirty,
  noAnswer,
  NotificationService,
  setControlEnabled,
  setControlValue,
  yesAnswer,
} from '@gh/core-ui';
import { ActivatedRoute } from '@angular/router';
import {
  ActionFlow,
  AppRouter,
  FormStateBuilder,
  getRouteInitData,
  IS_REGION_AU_OR_NZ,
  IS_REGION_US,
  NumberTypes,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { JobBilling } from '../../../resources/cm.model';
import {
  AbstractFormComponent,
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
  whenFormInvalid,
} from '@gh/builder-shared';
import { JobDetailsDatesTabFormState } from './job-details-billing-tab-form.service';
import { CmJobDetailsBusinessService } from '../../../resources/cm-job-details-business.service';
import { assign, isNumber } from 'lodash';
import { currentDate } from '@gh/core-util';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { filter$ } from '@gh/rx/operators';
import { FormControl } from '@angular/forms';
import {
  buildEditableCheckboxColumn,
  buildNumberColumn,
  GridDefinition,
  GridDefinitionFactory,
  typedValueFormatter,
} from '@gh/core-ui-grid';
import {
  CELL_NO_OVERFLOW_CLASS,
  CELL_TEXT_RIGHT_CLASS,
  createDefaultGridModel,
  innerLabelCellRendererDecorator,
  noneCellRenderer,
  ROW_DIRTY_CLASS,
} from '@gh/common-internal-shared';
import { JobBillingExcludedItemDto } from '@gh/builder-shared-data';
import { TranslationService } from '@gh/core-mls';
import { BehaviorSubject, Observable } from 'rxjs';

export type JobBillingExcludedElementOrTotal = JobBillingExcludedItemDto | JobBillingExcludedElementTotal;
export const getExcludedItemId = (item: JobBillingExcludedItemDto) => item.itemId;
export enum JobBillingExcludedElementType { Normal, Total = 'billingExcludedTotals' }
export type JobBillingExcludedElementTotal = {
  type: JobBillingExcludedElementType.Total;
  [key: string]: any;
};
export function isJobBillingExcludedElementTotal(element: JobBillingExcludedElementOrTotal): element is JobBillingExcludedElementTotal {
  return element['type'] && element['type'] === JobBillingExcludedElementType.Total;
}

@Component({
  selector:    'gh-job-details-billing-tab',
  template: require('./job-details-billing-tab.component.html'),
  styles:   [require('./job-details-billing-tab.component.scss')],
  providers:   [JobDetailsDatesTabFormState],
})
export class JobDetailsBillingTabComponent extends AbstractFormComponent<JobBilling> implements OnInit {
  jobId: ID;
  paidChecked: boolean;
  paidMasterChecked: boolean;
  billing: JobBilling;
  excludedItems: JobBillingExcludedItemDto[];
  originItems: JobBillingExcludedItemDto[];
  definition: GridDefinition;
  total$: Observable<any>;
  unitsCountList: number[] = [];
  countOfUnit = 15;
  _total = new BehaviorSubject<JobBillingExcludedElementTotal[]>([
    {type: JobBillingExcludedElementType.Total,  itemId: 'total-count'},
  ]);
  constructor(
    public form: JobDetailsDatesTabFormState,
    private route: ActivatedRoute,
    private formStateBuilder: FormStateBuilder,
    private businessService: CmJobDetailsBusinessService,
    private navigations: BuilderNavigationService,
    public userContext: BuilderUserContext,
    private pipelines: FormActionPipelines,
    private commonActions: CommonActions,
    private router: AppRouter,
    private notifications: NotificationService,
    private dialogService: DialogService,
    private translations: TranslationService,
    private gridDefinitionFactory: GridDefinitionFactory,
  ) {
    super();
    this.total$ = this._total.asObservable();
    this.paidChecked = false;
    this.paidMasterChecked = false;
    // set list from 1 to 10
    // tslint:disable-next-line:no-magic-numbers
    for (let i = 0; i < this.countOfUnit; i++) {
      this.unitsCountList.push(i + 1);
    }
  }

  get isUsRegion(): boolean {
    return IS_REGION_US;
  }

  get contractAmountLabel(): string {
    return IS_REGION_AU_OR_NZ ? 'lbl_ex_gst_dollar' : 'lbl_contract_amount_dollar';
  }

  get variationsAmountLabel(): string {
    return IS_REGION_AU_OR_NZ ? 'lbl_ex_gst_dollar' : 'lbl_job_change_orders_dollar';
  }

  get corpConfirmedNotSlabLabel(): string {
    return IS_REGION_US ? 'lbl_confirmed_not_slab' : 'lbl_corp_confirmed_not_slab';
  }

  get masterOfficeBilling(): string {
    return IS_REGION_US ? 'lbl_office_billing' : 'lbl_master_billing';
  }

  ngOnInit(): void {
    this.billing = getRouteInitData(this.route);
    assign(this, this.billing);
    if (this.billing.masterPaymentDue && isNumber(this.billing.masterPaymentDue)) {
      this.billing.masterPaymentDue = Number(this.billing.masterPaymentDue.toFixed(2));
    }
    this.form.build(this.billing);
    this.excludedItems = [...this.billing.items.map((item) => ({...item}))];
    this.originItems = [...this.billing.items.map((item) => ({...item}))];
    this.setTotals();
    this.definition = this.buildGridDefinition();
    if (!this.billing.isNotProceeding && !this.billing.isFinalised) {
      setControlEnabled(this.form.group, 'unitsCount', true);
    }
  }

  onSave(): void {
    if (this.form.value.isCorporateConfirmedNotSlab || this.form.value.isMasterConfirmedNotSlab) {
      const dirtyMaster = isControlDirty(this.form.group, 'isMasterConfirmedNotSlab');
      const dirtyCorporate = isControlDirty(this.form.group, 'isCorporateConfirmedNotSlab');
      if (dirtyMaster || dirtyCorporate) {
        if (dirtyMaster && dirtyCorporate && this.form.value.isMasterConfirmedNotSlab &&
          this.form.value.isCorporateConfirmedNotSlab) {
          const confirmationMaster$ = this.dialogService.confirmation({
            messageId:     'msg_confirm_not_slab',
            buttons:       [Confirmation.Yes, Confirmation.No, Confirmation.Cancel],
            defaultButton: Confirmation.Cancel,
          });
          confirmationMaster$.pipe(
            filter$(yesAnswer))
            .subscribe(() => {
              this.confirmationCorporate();
            });
          confirmationMaster$.pipe(
            filter$(noAnswer))
            .subscribe(() => {
              setControlValue(this.form.group, 'isMasterConfirmedNotSlab', false);
              this.confirmationCorporate();
            });
        } else {
          const confirmation$ = this.dialogService.confirmation({
            messageId:     this.form.value.isMasterConfirmedNotSlab ? 'msg_confirm_not_slab' : 'msg_corp_confirm_not_slab',
            buttons:       [Confirmation.Yes, Confirmation.No, Confirmation.Cancel],
            defaultButton: Confirmation.Cancel,
          });
          confirmation$.pipe(
            filter$(yesAnswer))
            .subscribe(() => {
              this.onProcess();
            });
          confirmation$.pipe(
            filter$(noAnswer))
            .subscribe(() => {
              if (this.form.value.isMasterConfirmedNotSlab) {
                setControlValue(this.form.group, 'isMasterConfirmedNotSlab', false);
              } else {
                setControlValue(this.form.group, 'isCorporateConfirmedNotSlab', false);
              }
            });
        }
      } else {
        this.onProcess();
      }
    } else {
      this.onProcess();
    }
  }

  confirmationCorporate(): void {
    const confirmationCorporate$ = this.dialogService.confirmation({
      messageId:     'msg_corp_confirm_not_slab',
      buttons:       [Confirmation.Yes, Confirmation.No, Confirmation.Cancel],
      defaultButton: Confirmation.Cancel,
    });
    confirmationCorporate$.pipe(
      filter$(yesAnswer))
      .subscribe(() => {
        this.onProcess();
      });
    confirmationCorporate$.pipe(
      filter$(noAnswer))
      .subscribe(() => {
        setControlValue(this.form.group, 'isCorporateConfirmedNotSlab', false);
        this.onProcess();
      });
  }

  get completeFlow(): ActionFlow<FormFlowState<JobBilling>> {
    return this.pipelines.complete({
      validateForm: this.commonActions.validateWithEffect(
        whenFormInvalid, () => this.handleErrors(),
      ),
      execute:      this.commonActions.execute((state: FormFlowState<JobBilling>) =>
        this.businessService.update(this.billing.jobId, {
            ...this.billing,
            ...this.form.value,
          items: this.getItemsChangesList(),
          },
        )),
      finish:       (state: FormFlowState<JobBilling>) => {
        const billingInfo = state.value;
        this.billing = billingInfo;
        this.excludedItems = [...this.billing.items.map((item) => ({...item}))];
        this.originItems = [...this.billing.items.map((item) => ({...item}))];
        assign(this, { billingInfo });
        this.form.build(billingInfo);
        if (!billingInfo.isNotProceeding && !billingInfo.isFinalised) {
          setControlEnabled(this.form.group, 'unitsCount', true);
        }
        this.setTotals();
        this.paidChecked = false;
        this.paidMasterChecked = false;
      },
    });
  }

  handleErrors(): void {
    setTimeout(() => {
      if (this.form) {
        this.form.group.markAllAsTouched();
        if (this.form.invalid) {
          for (const field in this.form.group.controls) {
            if (this.form.group.controls.hasOwnProperty(field)
              && this.form.group.controls[field].errors) {
              // @ts-ignore
              this.processNotification(this.form.getControl(field));
              return;
            }
          }
        }
      }
      // tslint:disable-next-line:no-magic-numbers
    },         100);
  }

  goToAel(): void {
    this.navigations.goToAel({
      entityId:   this.jobId || this.billing.jobId,
      entityType: 'JOB',
    });
  }

  onCancel(): void {
    const flow = this.deactivateFlow;
    flow.run({
      trigger: 'deactivate',
      form:    this.form,
      value:   this.form.submitValue,
    }).toFinalState().pipe(
      filter$(({ confirmation }) => Boolean(confirmation)))
      .subscribe(() => this.router.navigateBack());
  }

  togglePaid(event: MatCheckboxChange): void {
    this.paidChecked = event.checked;
    const dateValue = this.paidChecked ? currentDate() : this.billing.officeLastPaymentDate;
    // tslint:disable-next-line:no-null-keyword
    const officeAmountPaidValue = this.paidChecked ? getControlValue(this.form.group, 'officePaymentDue') : null;
    setControlValue(this.form.group, 'officeLastPaymentDate', dateValue);
    setControlValue(this.form.group, 'officePaidAmount', officeAmountPaidValue);
    if (!this.paidChecked) {
      this.form.getControl('officePaidAmount').clearValidators();
      this.form.getControl('officePaidAmount').updateValueAndValidity();
    }
  }

  toggleMasterPaid(event: MatCheckboxChange): void {
    this.paidMasterChecked = event.checked;
    const dateValue = this.paidMasterChecked ? currentDate() : this.billing.masterLastPaymentDate;
    // tslint:disable-next-line:no-null-keyword
    const masterAmountPaiValue = this.paidMasterChecked ? getControlValue(this.form.group, 'masterPaymentDue') : null;
    setControlValue(this.form.group, 'masterLastPaymentDate', dateValue);
    setControlValue(this.form.group, 'masterPaidAmount', masterAmountPaiValue);
    if (!this.paidMasterChecked) {
      this.form.getControl('masterPaidAmount').clearValidators();
      this.form.getControl('masterPaidAmount').updateValueAndValidity();
    }
  }

  toggleIsMasterConfirmedNotSlab(event: MatCheckboxChange): void {
    if (!event.checked) {
      setControlValue(this.form.group, 'isCorporateConfirmedNotSlab', false);
    }
  }

  toggleIsIsCorporateConfirmedNotSlab(event: MatCheckboxChange): void {
    if (!event.checked) {
      setControlValue(this.form.group, 'isMasterConfirmedNotSlab', false);
    }
  }

  private setTotals(): void {
    this._total.next([{ type: JobBillingExcludedElementType.Total, itemId: 'total-count', variationId: 'variation:footer',
      excludeItemExtVarDollar: this.billing.excludeItemTotalExtVarDollar,
      excludeItemExtNotVarDollar:  this.billing.excludeItemTotalExtNotVarDollar,
    }]);
  }

  private buildGridDefinition(): GridDefinition {
    const model = createDefaultGridModel({
      editable: true,
      getId: getExcludedItemId,
      command: (entity, name, value) => this.setExcludedItemField(entity, name, value),
    });
    return this.gridDefinitionFactory.buildManualWithModel({
      model,
      columns: [
        {
          field: `isItemExclude`,
          headerName: this.translations.get('lbl_exclude'),
          width: 120,
          ...buildEditableCheckboxColumn(model.getCellAccessor(`isItemExclude`)),
          pinnedRowCellRenderer: noneCellRenderer,
        },
        {
          field: `excludeItemCode`,
          headerName: this.translations.get('lbl_code'),
          width: 250,
        },
        {
          field: `excludeItemDesc`,
          headerName: this.translations.get('lbl_description'),
          width: 550,
        },
        {
          field: `excludeItemExtVarDollar`,
          headerName: this.translations.get('lbl_extension_var_dollar'),
          ...buildNumberColumn(NumberTypes.Price),
          width: 250,
          pinnedRowCellRenderer: innerLabelCellRendererDecorator(
            () => this.translations.get('lbl_total_extension_var_dollar'))(
            typedValueFormatter(NumberTypes.Price)),
          cellClassRules: {
            [CELL_NO_OVERFLOW_CLASS]: 'true',
            [CELL_TEXT_RIGHT_CLASS]: 'true',
          },
        },
        {
          field: `excludeItemExtNotVarDollar`,
          headerName: this.translations.get('lbl_extension_not_var_dollar'),
          ...buildNumberColumn(NumberTypes.Price),
          width: 250,
          pinnedRowCellRenderer: innerLabelCellRendererDecorator(
            () => this.translations.get('lbl_total_extension_not_var_dollar'))(
            typedValueFormatter(NumberTypes.Price)),
          cellClassRules: {
            [CELL_NO_OVERFLOW_CLASS]: 'true',
            [CELL_TEXT_RIGHT_CLASS]: 'true',
          },
        },
      ],
      options: {
        defaultColDef: {
          sortable: true,
        },
        getRowNodeId: (data) => data.itemId,
        rowClassRules: {
          [ROW_DIRTY_CLASS]: ({ data }: { data: JobBillingExcludedItemDto }) => {
            const targetOrigin = this.originItems.find((origin) => origin.itemId === data.itemId);
            return !!targetOrigin && targetOrigin.isItemExclude !== data.isItemExclude;
          },
        },
      },
    });
  }

  private processNotification(control: FormControl): void {
    if (control.hasError('required')) {
      this.notifications.error('msg_field_required');
      return;
    } else {
      this.notifications.error('msg_field_invalid_format');
    }
  }

  private getItemsChangesList(): JobBillingExcludedItemDto[] {
    const result = [] as JobBillingExcludedItemDto[];
    this.excludedItems.forEach((currentItem) => {
      const target = this.originItems.find((originItem) => originItem.itemId === currentItem.itemId);
      if (target && target.isItemExclude !== currentItem.isItemExclude) {
        result.push({ ...currentItem });
      }
    });
    return result;
  }

  private setExcludedItemField(entity: JobBillingExcludedItemDto, name: string, value: any): void {
    const result = [] as JobBillingExcludedItemDto[];
    this.excludedItems.forEach((item) => {
      if (item.itemId === entity.itemId) {
        item[name] = value;
      }
      result.push({...item});
    });
    this.excludedItems = result;
    const hasItemsChangesControl = this.form.getControl('items');
    hasItemsChangesControl.setValue(true);
    hasItemsChangesControl.markAsDirty();
  }
}
